<template>
  <div class="work-instructions-container">
    <WorkInstructionsHeader />

    <div class="wrapper">
      <div class="items-container" v-if="currentPackage">
        <div
          v-for="(instruction, wiIndex) in currentPackage.workInstructions ||
          []"
          :key="instruction._id"
        >
          <div class="card work-instruction-card">
            <div class="card-header">
              <div class="instruction-title">
                {{ wiIndex + 1 }}.
                <span>{{ instruction.title }}</span>
              </div>
            </div>

            <DisplayRichTextEditor :value="instruction.description" />

            <b-collapse animation="slide" :aria-id="instruction._id">
              <template #trigger="props">
                <div class="btn-expand">
                  <b-icon :icon="props.open ? 'menu-down' : 'menu-up'">
                  </b-icon>
                </div>
              </template>

              <div class="work-instruction-container">
                <template v-if="instruction.steps && instruction.steps.length">
                  <div
                    class="instruction wi-step card work-instruction-step-card"
                    v-for="(step, stepIndex) in instruction.steps"
                    :key="step._id || stepIndex"
                  >
                    <div class="instruction-title card-header">
                      {{ wiIndex + 1 }}.{{ stepIndex + 1 }}
                    </div>
                    <DisplayRichTextEditor :value="step.description" />

                    <div class="step-attachemnts-container">
                      <div class="columns">
                        <div class="column is-half">
                          <div class="attachment-label">
                            <img
                              src="/img/diagram_icon.png"
                              style="width: 24px; height: 24px"
                            />
                            Linked Tasks
                          </div>
                          <TasksDisplayGrid
                            :tasks="step.linkedTasks || []"
                            @task-click="onTaskClick"
                          />
                        </div>
                        <div class="column is-half">
                          <div class="attachment-label">
                            <b-icon icon="file-document-arrow-right"></b-icon>
                            Linked Files
                          </div>
                          <LinkedDocuments
                            v-if="
                              step.linkedDocuments &&
                              step.linkedDocuments.length > 0
                            "
                            :documents="step.linkedDocuments"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </b-collapse>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        Select a work instruction package to view its instructions.
      </div>
    </div>
  </div>
</template>

<script>
import { GET_JOB } from '../../store/jobs/actions/actionTypes';
import JobViewMixin from '../../mixins/Shared/JobViewMixin';
import WorkInstructionsHeader from '../Shared/WorkInstructions/Header';
import {
  FETCH_WORK_INSTRUCTION_PACKAGES,
  FETCH_WORK_INSTRUCTIONS_FOR_PACKAGE,
} from '../../store/workInstruction/actions/actionTypes';
import {
  GET_WORK_INSTRUCTION_PACKAGES,
  CURRENT_WORK_INSTRUCTION_PACKAGE,
} from '../../store/workInstruction/getters/getterTypes';
import { LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE } from '../../store/workInstruction/mutations/mutationTypes';
import DisplayRichTextEditor from '../RichTextEditor/DisplayRichTextEditor';
import LinkedDocuments from '../Shared/HyperlinkDocuments/LinkedDocuments';
import TasksDisplayGrid from '../Shared/TaskSelectModal/TasksDisplayGrid';

export default {
  mixins: [JobViewMixin],
  components: {
    WorkInstructionsHeader,
    DisplayRichTextEditor,
    LinkedDocuments,
    TasksDisplayGrid,
  },
  name: 'InspectorWorkInstructions',

  computed: {
    workInstructionPackages() {
      return this.$store.getters[GET_WORK_INSTRUCTION_PACKAGES];
    },
    currentPackage() {
      return this.$store.getters[CURRENT_WORK_INSTRUCTION_PACKAGE];
    },
  },

  watch: {
    currentPackage(newPackage) {
      if (newPackage) {
        this.$store.dispatch(
          FETCH_WORK_INSTRUCTIONS_FOR_PACKAGE,
          newPackage._id
        );
        this.syncPackageQueryParam(newPackage._id);
      } else {
        this.syncPackageQueryParam(null);
      }
    },
  },

  async mounted() {
    const { id } = this.$route.params;
    await this.$store.dispatch(GET_JOB, id);
    await this.$store.dispatch(FETCH_WORK_INSTRUCTION_PACKAGES, id);

    const packageId = this.normalizePackageId(this.$route.query.package);
    if (!packageId) {
      return;
    }

    const selectedPackage = this.workInstructionPackages.find(
      (wiPackage) => wiPackage._id === packageId
    );
    if (selectedPackage) {
      this.$store.commit(
        LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE,
        selectedPackage
      );
    }
  },

  methods: {
    normalizePackageId(value) {
      if (!value) {
        return null;
      }

      const normalizedValue = Array.isArray(value) ? value[0] : value;
      return String(normalizedValue)
        .trim()
        .replace(/^['"]|['"]$/g, '');
    },

    syncPackageQueryParam(packageId) {
      const normalizedPackageId = this.normalizePackageId(packageId);
      const currentPackageParam = this.normalizePackageId(
        this.$route.query.package
      );

      if (normalizedPackageId === currentPackageParam) {
        return;
      }

      const query = {
        ...this.$route.query,
      };

      if (normalizedPackageId) {
        query.package = normalizedPackageId;
      } else {
        delete query.package;
      }

      this.$router.replace({ query }).catch(() => {});
    },

    onTaskClick(task) {
      const { token } = this.$route.query;
      const query = token ? { token } : undefined;

      if (task.taskType === 'folder' && task.folderId) {
        this.$router.push({
          path: `/view/inspector/${this.currentJob._id}/folder/${task.folderId}/${task.taskNumber}`,
          query,
        });
        return;
      }

      if (task.taskType === 'folder') {
        this.$router.push({
          path: `/view/inspector/${this.currentJob._id}/folder/${task._id}`,
          query,
        });
        return;
      }

      const resolvedRoute = this.$router.resolve({
        name: 'inspector-template',
        params: { id: this.currentJob._id, taskNumber: task._id },
        query,
      });

      window.open(resolvedRoute.href, '_blank', 'noopener');
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  border-radius: 10px;
}

.work-instructions-container {
  height: calc(100vh - 50px);
  width: 100%;
}

.wrapper {
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  padding: 20px 10px;
  text-align: center;
  justify-content: center;
  width: 100%;
  background-color: #dddddd;
}

.items-container {
  flex: 1;
  overflow: auto;
  overflow-x: hidden;
  gap: 10px;
  display: flex;
  flex-direction: column;
  padding: 0 10px;
}

.instruction-title {
  display: flex;
  font-size: larger;
  font-weight: bold;
  flex-direction: row;
  width: 100%;
  border-radius: 10px;
  font-size: x-large;
  padding: 10px;

  span {
    white-space: pre-wrap;
    text-align: left;
  }
}

.wi-step {
  margin-left: 30px;
}

.work-instruction-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  background-color: #dddddd;
}

.step-attachemnts-container {
  margin: 10px;
  width: 100%;
}

.btn-expand {
  display: flex;
  justify-content: start;
  padding: 5px;
  cursor: pointer;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  background-color: white;
}

.collapse {
  background-color: #dddddd;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.attachment-label {
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  font-weight: 700;
  margin-bottom: 10px;
}

.empty-state {
  margin: auto;
  font-size: 18px;
  color: #555;
}
</style>
